import { IObservation, IObservation_Component, IPatient } from '@ahryman40k/ts-fhir-types/lib/R4';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { ObservationConstants } from '../../constants/fhir-observation.constants';
import {
  OBSERVATION_TYPES, MEASUREMENT_FILLER_TYPE_CONSTANTS, MEASUREMENT_FILLER_OBSERVATION_TYPES, RAW_FHIR_OBSERVATION,
  FHIR_OBSERVATION_CODE_BLOOD_PRESSURE, FHIR_OBSERVATION_VALUE_BLOODPRESSURE, FHIR_OBSERVATION_CODE_BODY_WEIGHT,
  FHIR_OBSERVATION_VALUE_BODYWEIGHT,
  FHIR_OBSERVATION_CODE_BLOOD_GLUCOSE_AFTER_MEAL,
  FHIR_OBSERVATION_CODE_BLOOD_GLUCOSE_PRE_MEAL,
  FHIR_OBSERVATION_CODE_BLOOD_GLUCOSE,
  FHIR_OBSERVATION_VALUE_BLOOD_GLUCOSE,
  BLOOD_GLUCOSE_TYPES,
  FHIR_OBSERVATION_CODE_NUTRITION,
  FHIR_OBSERVATION_VALUE_NUTRITION,
  FHIR_OBSERVATION_CODE_ACTIVITY_MIBAND,
  FHIR_OBSERVATION_CODE_ACTIVITY_POLAR,
  FHIR_OBSERVATION_VALUE_ACTIVITY_MIBAND,
  FHIR_OBSERVATION_VALUE_ACTIVITY_POLAR,
  FHIR_OBSERVATION_CODE_TRAINING_POLAR,
  FHIR_OBSERVATION_VALUE_TRAINING_POLAR,
  FHIR_OBSERVATION_CODE_BIOPARAMETERS,
  FHIR_OBSERVATION_VALUE_BIOPARAMETERS,
  FHIR_OBSERVATION_CODE_STEPPER,
  FHIR_OBSERVATION_VALUE_STEPPER
} from '../../constants/manual-data-recording.constants';
import { IObservationType, IMeasurementFillerTypes, IMeasurementFillerObservationTypes, IBloodGlucoseType, IHeartRateLvlForForm, IHeartRateLvlRangeForForm, IDurationForForm } from '../../models/manual-data-recording.models';

@Component({
  selector: 'inc-measurement-filler',
  templateUrl: './measurement-filler.component.html',
  styleUrls: ['./measurement-filler.component.scss'],
})
export class MeasurementFillerComponent implements OnInit, OnDestroy {
  @Input() type: string;
  @Input() color: string = 'default';
  @Input() observationTypes: IObservationType[] = OBSERVATION_TYPES;
  @Input() patient: IPatient;
  @Input() patientSubject: Subject<IPatient>;
  @Input() observation?: IObservation;

  @Output() onSave: EventEmitter<IObservation> = new EventEmitter();
  @Output() tajChangedEm: EventEmitter<any> = new EventEmitter();

  form: FormGroup;
  patientName = '';

  patientSubjectSub: Subscription;

  measurementFillerTypeConstants: IMeasurementFillerTypes = MEASUREMENT_FILLER_TYPE_CONSTANTS;
  measurementFillerObservationTypeConstants: IMeasurementFillerObservationTypes = MEASUREMENT_FILLER_OBSERVATION_TYPES;
  bloodGlucoseTypes: IBloodGlucoseType[] = BLOOD_GLUCOSE_TYPES;

  constructor() { }

  async ngOnInit() {
    this.form = this.createForm();
    if (this.type === this.measurementFillerTypeConstants.modify) {
      this.patchForm();
    }

    if (this.patient !== undefined && this.patient.name[0] !== undefined) {
      this.patientName = this.patient.name[0].text.trim();
    }

    this.patientSubjectSub = this.patientSubject.subscribe((patient: IPatient) => {
      this.patient = patient;
      this.patientName = patient.name[0].text.trim();
    });
  }

  ngOnDestroy(): void {
    if (this.patientSubjectSub) { this.patientSubjectSub.unsubscribe(); }
  }

  createForm(): FormGroup {
    const date = new Date();
    return new FormGroup({
      type: new FormControl(null, Validators.required),
      value: new FormGroup({}, Validators.required),
      taj: new FormControl(null, Validators.compose([Validators.required, Validators.pattern('(([0-9]{3}[-]{1}){2}[0-9]{3})?')])),
      date: new FormGroup({
        year: new FormControl(date.getFullYear(), Validators.compose([Validators.required, Validators.min(0)])),
        month: new FormControl(date.getMonth() + 1, Validators.compose([Validators.required, Validators.min(1), Validators.max(12)])),
        day: new FormControl(date.getDate(), Validators.compose([Validators.required, Validators.min(1), Validators.max(this.getMaxDateForThisMonth(date))])),
        hour: new FormControl(date.getHours(), Validators.compose([Validators.required, Validators.min(0), Validators.max(23)])),
        minute: new FormControl(date.getMinutes(), Validators.compose([Validators.required, Validators.min(0), Validators.max(59)]))
      })
    });
  }

  async resetForm(): Promise<void> {
    this.patient = undefined;
    this.patientName = '';
    this.patchForm(true);
  }

  setType(value: string): void {
    this.form.get('type').setValue(value);
    this.addMeasurementFormGroup(value);
  }

  monthChanged(event: any): void {
    if (event.detail && event.detail.value && this.form.get('date.month').valid) {
      const dayFormControl = this.form.get('date.day');
      dayFormControl.setValidators(Validators.compose([Validators.required, Validators.min(1), Validators.max(this.getMaxDateForThisMonth())]));
      dayFormControl.setValue(dayFormControl.value);
    }
  }

  tajChanged(event: any): void {
    if (event.detail && event.detail.value && this.form.get('taj').valid) {
      this.tajChangedEm.emit(this.form.get('taj').value);
    } else {
      this.clearPatient();
    }
  }

  clearPatient(): void {
    this.patient = undefined;
  }

  getMaxDateForThisMonth(date?: Date): number {
    const maxDate = new Date(date ? date : this.getDateFromFormGroup(this.form.get('date') as FormGroup));
    maxDate.setFullYear(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
    return maxDate.getDate();
  }

  getDateFromFormGroup(formGroup: FormGroup): Date {
    const date = new Date();
    date.setFullYear(formGroup.get('year').value, (formGroup.get('month').value - 1), formGroup.get('day').value);
    date.setHours(formGroup.get('hour').value, formGroup.get('minute').value, 0, 0);
    return date;
  }

  bmiCalc(): void {
    const weightValue = (this.form.controls.value as FormGroup).controls.weight.value;
    const heightValue = (this.form.controls.value as FormGroup).controls.height.value;
    (this.form.controls.value as FormGroup).controls.bmi.setValue(weightValue && heightValue ? (weightValue / Math.pow((heightValue / 100), 2)).toFixed(2) : null);
  }

  async onFinish(): Promise<void> {
    this.onSave.emit(await this.createFHIRObservation(this.type === this.measurementFillerTypeConstants.modify ? this.observation.id : null));
  }

  getHeartRateLevelDataFromComp(component: IObservation_Component): IHeartRateLvlForForm {
    return {
      value: component.valueQuantity.value,
      range: {
        high: { value: component.valueRange.high.value },
        low: { value: component.valueRange.low.value }
      }
    };
  }

  getHeartRateLevelFormGroup(): FormGroup {
    return new FormGroup({
      value: new FormControl(null, Validators.required),
      range: new FormGroup({
        high: new FormGroup({ value: new FormControl(null, Validators.required) }),
        low: new FormGroup({ value: new FormControl(null, Validators.required) })
      })
    });
  }

  getDurationFromComp(valueTime: string): IDurationForForm {
    const splitted1 = valueTime.split('H');
    const splitted2 = splitted1[1].split('M');
    const splitted3 = splitted2[1].split('.');
    return {
      hour: parseInt(splitted1[0].substring(2, splitted1[0].length), 10),
      minute: parseInt(splitted2[0], 10),
      second: parseInt(splitted3[0], 10),
      ms: parseInt(splitted3[1].substring(0, splitted3[1].length - 1), 10)
    };
  }

  getDurationFromForm(duration: IDurationForForm): string {
    return 'PT' + duration.hour + 'H' + duration.minute + 'M' + duration.second + '.' + duration.ms + 'S';
  }

  addMeasurementFormGroup(type: string): void {
    this.form.removeControl('value');
    // blood pressure
    if (type === this.measurementFillerObservationTypeConstants.bloodPressure) {
      this.form.addControl('value', new FormGroup({
        systolic: new FormControl(null, Validators.required),
        diastolic: new FormControl(null, Validators.required),
        pulse: new FormControl(null, Validators.required)
      }));
      // body weight
    } else if (type === this.measurementFillerObservationTypeConstants.bodyWeight) {
      this.form.addControl('value', new FormGroup({
        weight: new FormControl(null, Validators.required)
      }));
      // blood glucose
    } else if (type === this.measurementFillerObservationTypeConstants.bloodGlucose) {
      this.form.addControl('value', new FormGroup({
        glucose: new FormControl(null, Validators.required),
        glucoseType: new FormControl(null, Validators.compose([Validators.required, Validators.min(0), Validators.max(2)]))
      }));
      // nutrition
    } else if (type === this.measurementFillerObservationTypeConstants.nutrition) {
      this.form.addControl('value', new FormGroup({
        calorie: new FormControl(null, Validators.required),
        carbohydrate: new FormControl(null, Validators.required),
        logging: new FormControl(null, Validators.compose([Validators.required, Validators.min(0), Validators.max(5)]))
      }));
      // activity from MiBand
    } else if (type === this.measurementFillerObservationTypeConstants.activityMiband) {
      this.form.addControl('value', new FormGroup({
        steps: new FormControl(null, Validators.required),
        distance: new FormControl(0, Validators.required),
        burntCalories: new FormControl(0, Validators.required)
      }));
      // activity from Polar
    } else if (type === this.measurementFillerObservationTypeConstants.activityPolar) {
      this.form.addControl('value', new FormGroup({
        steps: new FormControl(null, Validators.required),
        burntCalories: new FormControl(null, Validators.required)
      }));
      // training from Polar
    } else if (type === this.measurementFillerObservationTypeConstants.trainingPolar) {
      this.form.addControl('value', new FormGroup({
        exerciseType: new FormControl(null, Validators.required),
        burntCalories: new FormControl(null, Validators.required),
        maxHeartRate: new FormControl(null, Validators.required),
        heartRateLvl1: this.getHeartRateLevelFormGroup(),
        heartRateLvl2: this.getHeartRateLevelFormGroup(),
        heartRateLvl3: this.getHeartRateLevelFormGroup(),
        heartRateLvl4: this.getHeartRateLevelFormGroup(),
        heartRateLvl5: this.getHeartRateLevelFormGroup(),
        intensity: new FormControl(null, Validators.required),
        duration: new FormGroup({
          hour: new FormControl(0, Validators.required),
          minute: new FormControl(0, Validators.compose([Validators.required, Validators.min(0), Validators.max(59)])),
          second: new FormControl(0, Validators.compose([Validators.required, Validators.min(0), Validators.max(59)])),
          ms: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)]))
        })
      }));
      // stepper
    } else if (type === this.measurementFillerObservationTypeConstants.stepper) {
      this.form.addControl('value', new FormGroup({
        steps: new FormControl(null, Validators.required),
        duration: new FormControl(null, Validators.required),
      }));
      // bioparameters
    } else if (type === this.measurementFillerObservationTypeConstants.bioparameters) {
      this.form.addControl('value', new FormGroup({
        systolic: new FormControl(null, Validators.required),
        diastolic: new FormControl(null, Validators.required),
        pulse: new FormControl(null, Validators.required),
        weight: new FormControl(null, Validators.required),
        height: new FormControl(null, Validators.required),
        bmi: new FormControl(null, Validators.required)
      }));
    } else {
      console.error('not implemented yet!');
    }
  }

  async patchForm(isReset?: boolean): Promise<void> {
    let type;
    let value;

    const date = new Date(this.observation.effectiveDateTime);

    // blood pressure
    if (this.observation.code.coding[0].code === ObservationConstants.BloodPressureCode) {
      type = this.measurementFillerObservationTypeConstants.bloodPressure;
      value = {
        systolic: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.SystolicCode).valueQuantity.value,
        diastolic: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.DiastolicCode).valueQuantity.value,
        pulse: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateCode).valueQuantity.value
      };
      // body weight
    } else if (this.observation.code.coding[0].code === ObservationConstants.BodyWeightCode) {
      type = this.measurementFillerObservationTypeConstants.bodyWeight;
      value = { weight: this.observation.valueQuantity.value };
      // blood glucose
    } else if (
      this.observation.code.coding[0].code === ObservationConstants.BloodGlucoseCode ||
      this.observation.code.coding[0].code === ObservationConstants.BloodGlucosePreMealCode ||
      this.observation.code.coding[0].code === ObservationConstants.BloodGlucosePostMealCode
    ) {
      type = this.measurementFillerObservationTypeConstants.bloodGlucose;
      value = {
        glucose: this.observation.valueQuantity.value,
        glucoseType: this.observation.code.coding[0].code === ObservationConstants.BloodGlucosePreMealCode ? 1 : this.observation.code.coding[0].code === ObservationConstants.BloodGlucosePostMealCode ? 2 : 0
      }
      // nutrition
    } else if (this.observation.code.coding[0].code === ObservationConstants.NutrientCode) {
      type = this.measurementFillerObservationTypeConstants.nutrition;
      value = {
        calorie: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.CalorieIntakePanelCode).valueQuantity.value,
        carbohydrate: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.CarbohydrateIntakePanelCode).valueQuantity.value,
        logging: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.LoggingActivityCode).valueQuantity.value
      };
      // activity from MiBand
    } else if (this.observation.code.coding[0].code === ObservationConstants.PhysicalActivityPanelCode) {
      type = this.measurementFillerObservationTypeConstants.activityMiband;
      value = {
        steps: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.StepActivityCode).valueQuantity.value,
        distance: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.DistanceCode).valueQuantity.value,
        burntCalories: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.BurntCaloriesActivityCode).valueQuantity.value
      };
      // activity from Polar
    } else if (this.observation.code.coding[0].code === ObservationConstants.ActivityCode) {
      type = this.measurementFillerObservationTypeConstants.activityPolar;
      value = {
        steps: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.StepActivityCode).valueQuantity.value,
        burntCalories: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.BurntCaloriesActivityCode).valueQuantity.value
      };
      // training from Polar
    } else if (this.observation.code.coding[0].code === ObservationConstants.RealtimePusoxiStepsCode) {
      type = this.measurementFillerObservationTypeConstants.trainingPolar;
      value = {
        exerciseType: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.ExerciseTypeCode).valueString,
        burntCalories: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.CaloriesBurnedCode).valueQuantity.value,
        maxHeartRate: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.MaxHeartBeatCode).valueQuantity.value,
        heartRateLvl1: this.getHeartRateLevelDataFromComp(this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateLvl1Code)),
        heartRateLvl2: this.getHeartRateLevelDataFromComp(this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateLvl2Code)),
        heartRateLvl3: this.getHeartRateLevelDataFromComp(this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateLvl3Code)),
        heartRateLvl4: this.getHeartRateLevelDataFromComp(this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateLvl4Code)),
        heartRateLvl5: this.getHeartRateLevelDataFromComp(this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateLvl5Code)),
        intensity: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.ExerciseIntensityCode).valueQuantity.value,
        duration: this.getDurationFromComp(this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.ExerciseDurationCode).valueTime)
      };
      // stepper
    } else if (this.observation.code.coding[0].code === ObservationConstants.SteppingSessionCode) {
      type = this.measurementFillerObservationTypeConstants.stepper;
      value = {
        steps: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.StepsTakenCode).valueQuantity.value,
        duration: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.TimePeriodStepsTakenCode).valueQuantity.value
      }
      // bioparameters
    } else if (this.observation.code.coding[0].code === ObservationConstants.BioparametersCode) {
      type = this.measurementFillerObservationTypeConstants.bioparameters;
      value = {
        systolic: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.SystolicCode).valueQuantity.value,
        diastolic: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.DiastolicCode).valueQuantity.value,
        pulse: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.HeartRateCode).valueQuantity.value,
        weight: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.BodyWeightCode).valueQuantity.value,
        height: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.BodyHeightCode).valueQuantity.value,
        bmi: this.observation.component.find(comp => comp.code.coding[0].code === ObservationConstants.BMIRatioCode).valueQuantity.value
      }
    }

    this.addMeasurementFormGroup(type);

    this.form.patchValue({
      type,
      value,
      taj: isReset ? '' : this.patient.identifier[0].value,
      date: {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes()
      }
    });

    this.monthChanged({ detail: { value: this.form.get('date.month').value } });
  }

  async createFHIRObservation(id?: string): Promise<IObservation> {
    let obs = Object.assign({}, RAW_FHIR_OBSERVATION);

    const formValue = this.form.getRawValue();
    const dateString = this.getDateFromFormGroup(this.form.get('date') as FormGroup).toISOString();

    // blood pressure
    if (formValue.type === this.measurementFillerObservationTypeConstants.bloodPressure) {
      obs.code = FHIR_OBSERVATION_CODE_BLOOD_PRESSURE;
      obs.component = FHIR_OBSERVATION_VALUE_BLOODPRESSURE;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.SystolicCode) {
          comp.valueQuantity.value = formValue.value.systolic;
        } else if (comp.code.coding[0].code === ObservationConstants.DiastolicCode) {
          comp.valueQuantity.value = formValue.value.diastolic;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateCode) {
          comp.valueQuantity.value = formValue.value.pulse;
        }
      });
      // body weight
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.bodyWeight) {
      obs.code = FHIR_OBSERVATION_CODE_BODY_WEIGHT;
      obs.valueQuantity = FHIR_OBSERVATION_VALUE_BODYWEIGHT;
      obs.valueQuantity.value = formValue.value.weight;
      // blood glucose
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.bloodGlucose) {
      obs.code = formValue.value.glucoseType === 1 ? FHIR_OBSERVATION_CODE_BLOOD_GLUCOSE_PRE_MEAL : formValue.value.glucoseType === 2 ? FHIR_OBSERVATION_CODE_BLOOD_GLUCOSE_AFTER_MEAL : FHIR_OBSERVATION_CODE_BLOOD_GLUCOSE;
      obs.valueQuantity = FHIR_OBSERVATION_VALUE_BLOOD_GLUCOSE;
      obs.valueQuantity.value = formValue.value.glucose;
      // nutrition
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.nutrition) {
      obs.code = FHIR_OBSERVATION_CODE_NUTRITION;
      obs.component = FHIR_OBSERVATION_VALUE_NUTRITION;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.CalorieIntakePanelCode) {
          comp.valueQuantity.value = formValue.value.calorie;
        } else if (comp.code.coding[0].code === ObservationConstants.CarbohydrateIntakePanelCode) {
          comp.valueQuantity.value = formValue.value.carbohydrate;
        } else if (comp.code.coding[0].code === ObservationConstants.LoggingActivityCode) {
          comp.valueQuantity.value = formValue.value.logging;
        }
      });
      // activity from MiBand
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.activityMiband) {
      obs.code = FHIR_OBSERVATION_CODE_ACTIVITY_MIBAND;
      obs.component = FHIR_OBSERVATION_VALUE_ACTIVITY_MIBAND;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.StepActivityCode) {
          comp.valueQuantity.value = formValue.value.steps;
        } else if (comp.code.coding[0].code === ObservationConstants.DistanceCode) {
          comp.valueQuantity.value = formValue.value.distance;
        } else if (comp.code.coding[0].code === ObservationConstants.BurntCaloriesActivityCode) {
          comp.valueQuantity.value = formValue.value.burntCalories;
        }
      });
      // activity from Polar
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.activityPolar) {
      obs.code = FHIR_OBSERVATION_CODE_ACTIVITY_POLAR;
      obs.component = FHIR_OBSERVATION_VALUE_ACTIVITY_POLAR;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.StepActivityCode) {
          comp.valueQuantity.value = formValue.value.steps;
        } else if (comp.code.coding[0].code === ObservationConstants.BurntCaloriesActivityCode) {
          comp.valueQuantity.value = formValue.value.burntCalories;
        }
      });
      // training from Polar
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.trainingPolar) {
      obs.code = FHIR_OBSERVATION_CODE_TRAINING_POLAR;
      obs.component = FHIR_OBSERVATION_VALUE_TRAINING_POLAR;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.ExerciseTypeCode) {
          comp.valueString = formValue.value.exerciseType
        } else if (comp.code.coding[0].code === ObservationConstants.CaloriesBurnedCode) {
          comp.valueQuantity.value = formValue.value.burntCalories;
        } else if (comp.code.coding[0].code === ObservationConstants.MaxHeartBeatCode) {
          comp.valueQuantity.value = formValue.value.maxHeartRate;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateLvl1Code) {
          comp.valueQuantity.value = formValue.value.heartRateLvl1.value;
          comp.valueRange.high.value = formValue.value.heartRateLvl1.range.high.value;
          comp.valueRange.low.value = formValue.value.heartRateLvl1.range.low.value;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateLvl2Code) {
          comp.valueQuantity.value = formValue.value.heartRateLvl2.value;
          comp.valueRange.high.value = formValue.value.heartRateLvl2.range.high.value;
          comp.valueRange.low.value = formValue.value.heartRateLvl2.range.low.value;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateLvl3Code) {
          comp.valueQuantity.value = formValue.value.heartRateLvl3.value;
          comp.valueRange.high.value = formValue.value.heartRateLvl3.range.high.value;
          comp.valueRange.low.value = formValue.value.heartRateLvl3.range.low.value;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateLvl4Code) {
          comp.valueQuantity.value = formValue.value.heartRateLvl4.value;
          comp.valueRange.high.value = formValue.value.heartRateLvl4.range.high.value;
          comp.valueRange.low.value = formValue.value.heartRateLvl4.range.low.value;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateLvl5Code) {
          comp.valueQuantity.value = formValue.value.heartRateLvl5.value;
          comp.valueRange.high.value = formValue.value.heartRateLvl5.range.high.value;
          comp.valueRange.low.value = formValue.value.heartRateLvl5.range.low.value;
        } else if (comp.code.coding[0].code === ObservationConstants.ExerciseIntensityCode) {
          comp.valueQuantity.value = formValue.value.intensity;
        } else if (comp.code.coding[0].code === ObservationConstants.ExerciseDurationCode) {
          comp.valueTime = this.getDurationFromForm(formValue.value.duration);
        }
      });
      // stepper
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.stepper) {
      obs.code = FHIR_OBSERVATION_CODE_STEPPER;
      obs.component = FHIR_OBSERVATION_VALUE_STEPPER;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.StepsTakenCode) {
          comp.valueQuantity.value = formValue.value.steps;
        } else if (comp.code.coding[0].code === ObservationConstants.TimePeriodStepsTakenCode) {
          comp.valueQuantity.value = formValue.value.duration;
        }
      });
      // bioparameters
    } else if (formValue.type === this.measurementFillerObservationTypeConstants.bioparameters) {
      obs.code = FHIR_OBSERVATION_CODE_BIOPARAMETERS;
      obs.component = FHIR_OBSERVATION_VALUE_BIOPARAMETERS;
      obs.component.forEach(comp => {
        if (comp.code.coding[0].code === ObservationConstants.SystolicCode) {
          comp.valueQuantity.value = formValue.value.systolic;
        } else if (comp.code.coding[0].code === ObservationConstants.DiastolicCode) {
          comp.valueQuantity.value = formValue.value.diastolic;
        } else if (comp.code.coding[0].code === ObservationConstants.HeartRateCode) {
          comp.valueQuantity.value = formValue.value.pulse;
        } else if (comp.code.coding[0].code === ObservationConstants.BodyWeightCode) {
          comp.valueQuantity.value = formValue.value.weight;
        } else if (comp.code.coding[0].code === ObservationConstants.BodyHeightCode) {
          comp.valueQuantity.value = formValue.value.height;
        } else if (comp.code.coding[0].code === ObservationConstants.BMIRatioCode) {
          comp.valueQuantity.value = formValue.value.bmi;
        }
      });
    } else {
      console.error('not implemented yet!');
    }

    if (this.patient) { obs.subject.reference = this.patient.id; }
    if (id) { obs.id = id; }

    obs.meta.lastUpdated = new Date().toISOString();

    obs.issued = dateString;
    obs.effectiveDateTime = dateString;

    return obs;
  }
}
